import type { SeriesPeriod } from "./series-period";

export const IP_BLOCKING_SERIES_CONTRACT = { name: "ip-blocking-series", version: 1 } as const;

export interface IpBlockingPoint {
  day: string;
  detected: number | null;
  sent: number | null;
  confirmedBlocked: number | null;
  safeToBlock?: number | null;
  /** Regulator-approved targets, whatever their safety evidence. */
  approved?: number | null;
}

export interface IpBlockingFreshness {
  generatedAt: string;
  dataThrough: string;
  source: string;
  isEstimated: boolean;
}

export interface IpBlockingSendingCounts {
  eligibleSendings: number;
  blockedSendings: number;
  ratio: number | null;
}

export interface IpBlockingIsp extends IpBlockingSendingCounts {
  isp: string;
  country?: string | null;
}

export type IpBlockingStatus =
  | "not_checked"
  | "blocked"
  | "legal_redirection"
  | "not_blocked"
  | "other";

export interface IpBlockingSeriesData {
  targetKind?: "IP" | "DOMAIN";
  /** Tickets selected by creation time, or by blocking windows overlapping the period. */
  dateBasis?: "creation" | "blocking_window";
  platformSafety?: { web: number | null; iptv: number | null };
  points: IpBlockingPoint[];
  freshness: IpBlockingFreshness | null;
  period?: SeriesPeriod | null;
  coverage?: {
    correlated: boolean;
    detectionTenantIds: number[];
    sentTenantIds: number[];
    confirmationTenantIds: number[];
  };
  /** Creation-cohort totals are distinct over the period, never daily sums. */
  cohorts?: {
    /** `approved` counts safe-only targets whose latest ticket has `is_regulator_approved`. */
    total: { detected: number | null; sent: number | null; approved?: number | null };
    observedTenantIds: number[];
  };
  /** Approved ticket/ISP sendings, separate from the IP-volume population. */
  effectiveness?: {
    total: IpBlockingSendingCounts;
    daily: (IpBlockingSendingCounts & { day: string })[];
    isps: IpBlockingIsp[];
    topIsps: IpBlockingIsp[];
    bottomIsps: IpBlockingIsp[];
    eligibleTargets?: number | null;
    blockedTargets?: number | null;
    majorityRatio?: number | null;
  };
  /** Approved ticket/ISP sending hours under each prevailing PBC status. */
  statusHours?: { status: IpBlockingStatus; hours: number }[] | null;
  /** Safe-only versus Not confirmed safe; retain all four evidence groups. */
  safety?: {
    detected: number | null;
    safe: number | null;
    notConfirmedSafe: number | null;
    ratio: number | null;
    safeOnly: number | null;
    unsafeOnly: number | null;
    conflicting: number | null;
    unknown: number | null;
  };
}
